import type { Viewer } from '@xeokit/xeokit-sdk';

interface RawMetaObject {
  id: string | number;
  type?: string;
  parent?: string | number | null;
  children?: number[];
}

interface MetaSceneObject {
  id: string | number;
  type?: string;
  children?: MetaSceneObject[];
}

interface SceneEntity {
  id: string;
  originalSystemId?: string | number;
}

interface ModelWithMetadata {
  metadata?: { metaObjects?: RawMetaObject[] };
  objects?: Record<string, SceneEntity>;
}

interface MetaSceneModel {
  metaObjects?: { [id: string]: MetaSceneObject };
}

interface NormalizedNode {
  id: string;
  type?: string;
  childIds: string[];
}

interface ModelMeta {
  childIdsById: Map<string, string[]>;
  typeById: Map<string, string | undefined>;
  entityIdByMetaId: Map<string, string>;
  subtreeMetaIds: (rootId: string) => string[];
}

const metaCache = new WeakMap<object, ModelMeta>();

function nodesFromMetadata(metaObjects: RawMetaObject[]): NormalizedNode[] {
  const nodesById = new Map<string, NormalizedNode>();
  const nodes = metaObjects.map((metaObject) => {
    const node: NormalizedNode = { id: String(metaObject.id), type: metaObject.type, childIds: [] };
    nodesById.set(node.id, node);
    return node;
  });

  metaObjects.forEach((metaObject, index) => {
    const node = nodes[index];
    if (Array.isArray(metaObject.children) && metaObject.children.length) {
      for (const childIndex of metaObject.children) {
        const child = metaObjects[childIndex];
        if (child) node.childIds.push(String(child.id));
      }
    } else if (metaObject.parent !== undefined && metaObject.parent !== null) {
      const parent = nodesById.get(String(metaObject.parent));
      if (parent && parent.id !== node.id) parent.childIds.push(node.id);
    }
  });

  return nodes;
}

function nodesFromMetaScene(metaModel: MetaSceneModel): NormalizedNode[] {
  const metaObjects = metaModel.metaObjects ?? {};
  return Object.values(metaObjects).map((metaObject) => ({
    id: String(metaObject.id),
    type: metaObject.type,
    childIds: Array.isArray(metaObject.children)
      ? metaObject.children.map((child) => String(child.id))
      : [],
  }));
}

function entityResolver(model: ModelWithMetadata): Map<string, string> {
  const map = new Map<string, string>();
  const objects = model.objects ?? {};

  for (const entityId in objects) {
    map.set(String(objects[entityId].id), String(objects[entityId].id));
  }

  for (const entityId in objects) {
    const entity = objects[entityId];
    if (entity.originalSystemId != null)
      map.set(String(entity.originalSystemId), String(entity.id));
  }

  return map;
}

function buildModelMeta(nodes: NormalizedNode[], entityIdByMetaId: Map<string, string>): ModelMeta {
  const childIdsById = new Map<string, string[]>();
  const typeById = new Map<string, string | undefined>();

  for (const node of nodes) {
    childIdsById.set(node.id, node.childIds);
    typeById.set(node.id, node.type);
  }

  const subtreeMetaIds = (rootId: string): string[] => {
    if (!childIdsById.has(rootId)) return [];
    const result: string[] = [];
    const seen = new Set<string>();
    const stack = [rootId];
    while (stack.length) {
      const id = stack.pop()!;
      if (seen.has(id)) continue;
      seen.add(id);
      result.push(id);
      const children = childIdsById.get(id);
      if (children) for (const childId of children) stack.push(childId);
    }
    return result;
  };

  return { childIdsById, typeById, entityIdByMetaId, subtreeMetaIds };
}

function modelMeta(
  model: ModelWithMetadata,
  metaModel: MetaSceneModel | undefined,
): ModelMeta | undefined {
  const metaObjects = model?.metadata?.metaObjects;

  let source: object | undefined;
  let nodes: NormalizedNode[] | undefined;

  if (Array.isArray(metaObjects) && metaObjects.length) {
    source = model.metadata;
    nodes = nodesFromMetadata(metaObjects);
  } else if (metaModel?.metaObjects && Object.keys(metaModel.metaObjects).length) {
    source = metaModel;
    nodes = nodesFromMetaScene(metaModel);
  }

  if (!source || !nodes) return undefined;

  let meta = metaCache.get(source);
  if (!meta) {
    meta = buildModelMeta(nodes, entityResolver(model));
    metaCache.set(source, meta);
  }
  return meta;
}

function modelMetas(viewer: Viewer): ModelMeta[] {
  const models = viewer.scene.models as unknown as Record<string, ModelWithMetadata>;
  const metaModels = (
    viewer.metaScene as unknown as { metaModels?: Record<string, MetaSceneModel> }
  )?.metaModels;

  const metas: ModelMeta[] = [];
  for (const modelId in models) {
    const meta = modelMeta(models[modelId], metaModels?.[modelId]);
    if (meta) metas.push(meta);
  }
  return metas;
}

/** Scene Entity ids of the subtree rooted at the given metaObject id (an Entity's originalSystemId). */
export function getMetaSubtreeEntityIds(viewer: Viewer, metaId: string): string[] {
  for (const meta of modelMetas(viewer)) {
    if (!meta.childIdsById.has(metaId)) continue;
    const ids: string[] = [];
    for (const subtreeMetaId of meta.subtreeMetaIds(metaId)) {
      const entityId = meta.entityIdByMetaId.get(subtreeMetaId);
      if (entityId) ids.push(entityId);
    }
    return ids;
  }
  return [];
}

/** Scene Entity ids of all metaObjects of the given type. */
export function getEntityIdsByType(viewer: Viewer, type: string): string[] {
  const ids: string[] = [];
  for (const meta of modelMetas(viewer)) {
    for (const [metaId, objectType] of meta.typeById) {
      if (objectType !== type) continue;
      const entityId = meta.entityIdByMetaId.get(metaId);
      if (entityId) ids.push(entityId);
    }
  }
  return ids;
}

/** Map of Scene Entity id -> metaObject type. */
export function getTypeByEntityId(viewer: Viewer): Map<string, string | undefined> {
  const map = new Map<string, string | undefined>();
  for (const meta of modelMetas(viewer)) {
    for (const [metaId, type] of meta.typeById) {
      const entityId = meta.entityIdByMetaId.get(metaId);
      if (entityId) map.set(entityId, type);
    }
  }
  return map;
}

export function getMetaObjectTypes(viewer: Viewer): string[] {
  const types = new Set<string>();
  for (const meta of modelMetas(viewer)) {
    for (const type of meta.typeById.values()) if (type) types.add(type);
  }
  return [...types];
}
